import React, { useState, useCallback, useMemo, useRef } from "react";
import cx from "classnames";
import styles from "./InvoiceEmail.module.scss";
import MailCheck from "react-mailcheck";
import { useTranslation } from "react-i18next";
import { useContractId } from "../../../../hooks/useContractId";
import { useContract } from "../../../../hooks/useContract";
import { useAtomValue, useSetAtom } from "jotai";
import {
  contractErrorState,
  contractSaveState,
} from "../../../../state/contractSaveState";
import { useSuspenseQueries, useMutation } from "@tanstack/react-query";
import { dataAssociates } from "../../../../data/dataAssociates";
import { usePrimaryContact } from "../../../../hooks/usePrimaryContact";
import { ContractSaveError, handleError } from "../ContractPage";
import { dataContracts } from "../../../../data/dataContracts";
import { ONGOING_RESPONSE } from "../../../../data/models/CommonTypes";
import { ServerError } from "../../../../network/API";
import { UNAUTHORIZED_STATUS_CODE } from "../../../../data/data";
import { ContractEditError } from "../ContractEditError";
import { Form } from "../../../../components/form/Form";
import { FormName } from "../menus/ContractEditMenu";
import { hasRealErrors } from "../../../../components/form/FormHelpers";
import { TextInput } from "../../../../components/form/TextInput";
import { RequiredValidator } from "../../../../components/form/validators/RequiredValidator";
import { EmailValidator } from "../../../../components/form/validators/EmailValidator";
import { T } from "../../../../components/translation/T";
import { contractInvoiceState } from "../../../../state/contractInvoiceState";
import { InvoiceType } from "../../../../data/models/ContractTypes";

interface Props {}

const domains = [
  "gmail.com",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "telia.com",
  "comhem.se",
  "bredband.net",
  "spray.se",
  "bahnhof.se",
  "tele2.se",
  "swipnet.se",
  "stofanet.dk",
  "tdc.dk",
  "youmail.dk",
  "online.no",
  "broadpark.no",
  "getmail.no",
  "kolumbus.fi",
  "elisanet.fi",
  "saunalahti.fi",
  "pp.inet.fi",
];

const secondLevelDomains = ["yahoo", "hotmail", "mail", "live", "outlook"];

const topLevelDomains = ["com", "net", "org", "se", "dk", "no", "fi"];

export const InvoiceEmail: React.FunctionComponent<Props> = () => {
  const invoiceType = useAtomValue(contractInvoiceState);
  const contractId = useContractId();
  const contract = useContract();
  const setDataError = useSetAtom(contractErrorState);
  const setDataSaved = useSetAtom(contractSaveState);
  const { t } = useTranslation();

  const onClose = useCallback(() => setError(null), []);
  const [{ data: associates }] = useSuspenseQueries({
    queries: [dataAssociates.fetchAssociates(contractId)],
  });

  const originalPrimaryContact = usePrimaryContact(associates);
  const [invoiceEmail, setInvoiceEmail] = useState<string | undefined>(
    contract.contractData.invoiceEmail
  );
  const primaryContactEmail = originalPrimaryContact?.contact?.email;

  const enableCopyEmail = useMemo(() => {
    return primaryContactEmail !== contract.contractData.invoiceEmail;
  }, [contract.contractData.invoiceEmail, primaryContactEmail]);

  const [error, setError] = useState<ContractSaveError | null>(null);
  const latestSaved = useRef<string>(invoiceEmail);
  const latestErroneousSaved = useRef<string | null>(null);
  const [displayableError, setDisplayableError] = useState<
    string | undefined
  >();
  const saveTimer = useRef<number>(undefined);

  const { mutate: trySaveEmail } = useMutation({
    mutationFn: (invoiceEmail: string) => {
      setDisplayableError(undefined);
      if (invoiceEmail === latestSaved.current) {
        return Promise.resolve(invoiceEmail);
      }

      return dataContracts.saveInvoiceEmail(contractId, invoiceEmail);
    },
    onSuccess: () => {
      latestSaved.current = invoiceEmail;
      setDataSaved((dataSaved) =>
        dataSaved.concat({
          date: new Date(),
        })
      );
    },
    onError: (err: unknown, updated: string, context) => {
      if (err === ONGOING_RESPONSE) {
        return;
      }

      if ((err as ServerError<unknown>).status === UNAUTHORIZED_STATUS_CODE) {
        return;
      }

      const untypedError = err as any;

      if (typeof untypedError?.data === "string") {
        setDisplayableError(untypedError?.data);
      } else if (typeof untypedError?.data?.message === "string") {
        setDisplayableError(untypedError?.data?.message);
      } else {
        setDisplayableError(undefined);
      }

      latestErroneousSaved.current = updated;
      handleError(err as ServerError<unknown>, setError);
      setInvoiceEmail(latestSaved.current as string);
      setDataError((dataErrors) =>
        dataErrors.concat({
          date: new Date(),
        })
      );
    },
  });

  const saveEmail = useCallback(
    (email: string) => {
      clearTimeout(saveTimer.current);

      saveTimer.current = window.setTimeout(() => {
        trySaveEmail(email);
      }, 500);
    },
    [trySaveEmail]
  );

  const retryEmailSave = useCallback(() => {
    if (!latestErroneousSaved.current) {
      return;
    }

    setError(null);

    setTimeout(() => {
      if (!latestErroneousSaved.current) {
        return;
      }

      trySaveEmail(latestErroneousSaved.current as string);
    }, 500);
  }, [trySaveEmail]);

  return (
    <div>
      <ContractEditError
        error={error}
        displayableError={displayableError}
        retry={latestErroneousSaved.current ? retryEmailSave : undefined}
        onClose={onClose}
      />

      <Form
        name={FormName.INVOICE_EMAIL}
        onSaveTrigger={(_, form) => {
          const realErrors = hasRealErrors(form);

          if (realErrors) {
            return;
          }

          saveEmail(invoiceEmail || "");
        }}
      >
        <div className="p-top-10">
          <MailCheck
            email={invoiceEmail || ""}
            domains={domains}
            topLevelDomains={topLevelDomains}
            secondLevelDomains={secondLevelDomains}
          >
            {(suggestion) => (
              <div>
                <div className="tablet-columns">
                  <div>
                    <TextInput
                      className={cx(styles.invoiceEmail)}
                      onChange={(value) => {
                        setInvoiceEmail(value);
                      }}
                      value={invoiceEmail}
                      name="email"
                      label="Invoice email"
                      validators={
                        invoiceType.invoiceType === InvoiceType.ADDRESS
                          ? []
                          : [
                              new RequiredValidator("Email is required"),
                              new EmailValidator("Email is not valid"),
                            ]
                      }
                    />
                  </div>
                  <div
                    className={cx(styles.copyEmail, {
                      [styles.show]: !!primaryContactEmail,
                    })}
                  >
                    {t("Use ")}{" "}
                    <button
                      disabled={
                        !enableCopyEmail || invoiceEmail === primaryContactEmail
                      }
                      onClick={(ev) => {
                        ev.preventDefault();
                        setInvoiceEmail(primaryContactEmail);
                        if (!primaryContactEmail) {
                          return;
                        }
                        saveEmail(primaryContactEmail);
                      }}
                    >
                      {primaryContactEmail}
                    </button>
                  </div>
                </div>

                {suggestion && (
                  <div className={styles.warning}>
                    <T>Did you mean</T>{" "}
                    <button
                      onClick={(ev) => {
                        ev.preventDefault();
                        setInvoiceEmail(suggestion.full);
                        saveEmail(suggestion.full);
                      }}
                    >
                      {suggestion.full}
                    </button>
                    ?
                  </div>
                )}
              </div>
            )}
          </MailCheck>
        </div>
      </Form>
    </div>
  );
};
